import { TFunction } from 'i18next';
import { TenantProcess } from '../tenantProcessStateBase';
import {
  AIActionKind,
  AIAssistantActionTemplate,
  AIProcessActionTemplate,
  AIPromptActionTemplate,
} from './aiActionTemplate';

export type ProcessStartParams = {
  processId: TenantProcess;
  runsInBackground?: boolean;
};

// A switch over literal keys: the translation scanner cannot follow a key built from the process name.
const getProcessLabel = (t: TFunction<string[]>, processId: TenantProcess): string => {
  switch (processId) {
    case TenantProcess.AnnualPlan:
      return t('copilot:Actions.AnnualPlan.Label');
    case TenantProcess.Library:
      return t('copilot:Actions.Library.Label');
    case TenantProcess.ProcessSuggestions:
      return t('copilot:Actions.Processes.Label');
    case TenantProcess.ControlEvidenceAudit:
      return t('copilot:Actions.ControlEvidenceAudit.Label');
    case TenantProcess.ControlDocumentationAudit:
      return t('copilot:Actions.ControlDocumentationAudit.Label');
    case TenantProcess.TaskEvidenceAudit:
      return t('copilot:Actions.TaskEvidenceAudit.Label');
    case TenantProcess.AuditFindings:
      return t('copilot:Actions.AuditFindings.Label');
    case TenantProcess.FrameworkAudit:
      return t('copilot:Actions.FrameworkAudit.Label');
    case TenantProcess.AutomationDrafting:
      return t('copilot:Actions.DraftAutomation.Label');
    case TenantProcess.SupplierIncidents:
      return t('copilot:Actions.SupplierIncidents.Label');
    case TenantProcess.SupplierCompliance:
      return t('copilot:Actions.SupplierCompliance.Label');
    case TenantProcess.EntityEvidenceAudit:
      return t('copilot:Actions.EntityEvidenceAudit.Label');
    default:
      return '';
  }
};

/**
 * Opens the panel on one copilot process. The anchor is the process name, so two processes bound on
 * one usage get distinct binding ids.
 */
export const processStartTemplate: AIProcessActionTemplate<ProcessStartParams> = {
  id: 'process.start',
  kind: AIActionKind.Process,
  getLabel(t, params) {
    return getProcessLabel(t, params.processId);
  },
  getAnchor(params) {
    return TenantProcess[params.processId];
  },
  getProcessId(params) {
    return params.processId;
  },
  runsInBackground(params) {
    return params.runsInBackground === true;
  },
};

// Opens the panel and leaves the choice to the user.
export const askAssistantTemplate: AIAssistantActionTemplate = {
  id: 'assistant.ask',
  kind: AIActionKind.Assistant,
  getLabel(t) {
    return t('copilot:Panel.AskAi');
  },
};

/**
 * The one help question of a scene: what the page is for, the current state of its data and what to do
 * next. The scene-help skill of the general knowledge loop reads the page from the scene name, the entity
 * or the context key of the turn.
 */
export const sceneHelpTemplate: AIPromptActionTemplate = {
  id: 'scene.help',
  kind: AIActionKind.Prompt,
  getLabel(t) {
    return t('copilot:Actions.SceneHelp.Label');
  },
  getPrompt(t) {
    return t('copilot:Actions.SceneHelp.Prompt');
  },
};

/**
 * The audit scene's ready-made questions for the audit agent. Each puts its question in the
 * conversation; the agent decides what to change and reports back. One template per question,
 * because a parameterised prompt would need a variable translation key.
 */
export const auditDivideWorkTemplate: AIPromptActionTemplate = {
  id: 'audit.divideWork',
  kind: AIActionKind.Prompt,
  getLabel(t) {
    return t('copilot:Actions.AuditPreparation.DivideWork.Label');
  },
  getPrompt(t) {
    return t('copilot:Actions.AuditPreparation.DivideWork.Prompt');
  },
};

export const auditAssignProceduresTemplate: AIPromptActionTemplate = {
  id: 'audit.assignProcedures',
  kind: AIActionKind.Prompt,
  getLabel(t) {
    return t('copilot:Actions.AuditPreparation.AssignProcedures.Label');
  },
  getPrompt(t) {
    return t('copilot:Actions.AuditPreparation.AssignProcedures.Prompt');
  },
};

export const auditStatusTemplate: AIPromptActionTemplate = {
  id: 'audit.status',
  kind: AIActionKind.Prompt,
  getLabel(t) {
    return t('copilot:Actions.AuditPreparation.Status.Label');
  },
  getPrompt(t) {
    return t('copilot:Actions.AuditPreparation.Status.Prompt');
  },
};
